import { format } from "date-fns";

import { Info } from "@livechat/design-system-icons";
import { DesignToken, Icon, Tooltip } from "@livechat/design-system-react-components";

import { AccordionItem } from "components/accordion-item/AccordionItem";
import { Label } from "components/custom-section";
import { DateFormat } from "constants/date";
import { getIsMemberOfTicketTeam, getLastMessageEvent, getLiveChatTicketId } from "helpers/tickets";
import { ITicket, TicketPriority, TicketStatus } from "interfaces/entities/ticket";
import { IAgent } from "store/entities/agents";
import { ITeam } from "store/entities/teams";
import { withVar } from "themes/helpers";

import { TicketDetailsInfoTicketId } from "./TicketDetailsInfoTicketId";
import { LanguageDetectionPromo } from "./info/LanguageDetectionPromo";
import { TicketLanguageLabel } from "./info/TicketLanguageLabel";
import { TicketRatingLabel } from "./info/TicketRatingLabel";
import { TicketSourceLabel } from "./info/TicketSourceLabel";
import { SectionWrapper, SourceWrapper, StyledLabelWithTooltip } from "./styles";
import PrioritySwitcher from "./switchers/PrioritySwitcher";
import StatusSwitcher from "./switchers/StatusSwitcher";

interface IProps {
  ticket: ITicket;
  teams: ITeam[];
  canManageTicket: boolean;
  currentAgent: IAgent;
  onTicketUrlCopy(): void;
  onStatusUpdate(status: TicketStatus): void;
  onPriorityUpdate(priority: TicketPriority): void;
  opened: boolean;
  onHeaderClick(): void;
}

export const TicketDetailsInfo: React.FC<IProps> = ({
  ticket,
  canManageTicket,
  currentAgent,
  onTicketUrlCopy,
  onPriorityUpdate,
  onStatusUpdate,
  opened,
  onHeaderClick,
}) => {
  const lastMessage = getLastMessageEvent(ticket.events);
  const liveChatTicketId = getLiveChatTicketId(ticket);
  const isMemberOfTicketTeam = getIsMemberOfTicketTeam(currentAgent, ticket);
  const modificationsDisabled = !canManageTicket || !isMemberOfTicketTeam;

  return (
    <SectionWrapper>
      <AccordionItem title="Ticket info" opened={opened} onHeaderClick={onHeaderClick}>
        <Label
          label="Ticket ID:"
          value={<TicketDetailsInfoTicketId id={ticket.shortId} onTicketUrlCopied={onTicketUrlCopy} />}
        />
        {liveChatTicketId && (
          <Label
            label="LiveChat Ticket ID:"
            value={
              <StyledLabelWithTooltip>
                {liveChatTicketId}
                <Tooltip
                  kind="invert"
                  placement="left"
                  triggerRenderer={
                    <span className="tooltip">
                      <Icon source={Info} size="small" customColor={withVar(DesignToken.ContentBasicPrimary)} />
                    </span>
                  }
                >
                  This ticket was imported from LiveChat Tickets.
                </Tooltip>
              </StyledLabelWithTooltip>
            }
          />
        )}
        <Label
          label="Created:"
          value={format(ticket.date, DateFormat.ShortDate)}
          title={format(ticket.date, DateFormat.DateTime)}
        />
        {lastMessage && (
          <Label
            label="Last message:"
            value={format(lastMessage.date, DateFormat.ShortDate)}
            title={format(lastMessage.date, DateFormat.DateTime)}
          />
        )}
        <Label
          label="Status:"
          value={<StatusSwitcher ticket={ticket} onStatusUpdate={onStatusUpdate} disabled={modificationsDisabled} />}
        />
        <Label
          label={ticket.totalRatings > 1 ? "Recent rating:" : "Rating:"}
          value={
            <StyledLabelWithTooltip>
              <TicketRatingLabel
                ratingRequestSent={ticket.ratingRequestSent}
                rating={ticket.rating ?? null}
                status={ticket.status}
              />
            </StyledLabelWithTooltip>
          }
        />
        <Label
          label="Priority:"
          value={
            <PrioritySwitcher ticket={ticket} onPriorityUpdate={onPriorityUpdate} disabled={modificationsDisabled} />
          }
        />
        <SourceWrapper className="fs-mask">
          <Label label="Source:" value={<TicketSourceLabel source={ticket.source} />} />
        </SourceWrapper>
        <Label label="Language:" value={<TicketLanguageLabel detectedLanguage={ticket.detectedLanguage} />} />
        {ticket.detectedLanguage && <LanguageDetectionPromo />}
      </AccordionItem>
    </SectionWrapper>
  );
};
